import { apiClient, toServiceError, type ManualCommandConfig } from '@/services/http/client'

export type VaultService = 'income_tax' | 'gst' | 'finance'
export interface Page<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}
export interface PortableProfile {
  display_name: string
  contact_email?: string | null
  contact_phone?: string | null
  postal_address?: string | null
  pan?: string | null
  date_of_birth?: string | null
  business_registration_number?: string | null
}
export interface ProfileVersion {
  version_id: string
  version_number: number
  basis: string
  read_only: boolean
  created_at?: string
}
export interface Profile extends ProfileVersion {
  profile: PortableProfile
  allowed_actions: string[]
  client_status: string
}
export interface VaultFile {
  version_id: string
  resource_id: string
  version_number: number
  filename: string
  classification: 'client_controlled' | 'client_output'
  category: string
  service: VaultService
  financial_year: string | null
  state: string
  byte_size: number
  content_sha256: string
  origin_firm_id: string | null
  basis?: string
  read_only: boolean
}
export interface VaultEnrollment {
  operation_id: string
  client_id: string
  resource_id: string
  version_id: string
  scan_job_id: string
  state: 'pending_scan'
}
export interface PendingVaultUploads {
  items: Array<{ job_id: string; client_id: string; version_id: string; created_at: string }>
  next_cursor: string | null
}
export interface VaultPublication {
  publication_id: string
  job_id: string
  client_id: string
  version_id: string
  registration_receipt_id: string
  published_at: string
  state: 'published'
}
export type VaultUpload = VaultFile | VaultEnrollment
export const scanJobPath = (job: string) => `/client-vault/scan-jobs/${id(job)}:publish`
export interface GrantScope {
  actions: string[]
  services: VaultService[]
  categories: string[]
  financial_years: string[]
}
export interface Relationship {
  relationship_id: string
  firm_id: string
  status: string
  revision: number
  engagements: {
    engagement_id: string
    service: VaultService
    status: string
    grants: {
      grant_id: string
      revision: number
      status: string
      scope: GrantScope
      expires_at?: string | null
    }[]
  }[]
}
export interface Switch {
  switch_id: string
  client_id: string
  source_firm_id: string
  target_firm_id: string
  status: string
  revision: number
  expires_at: string
  terms_version: string
  targets: { service: VaultService; scope: GrantScope; grant_expires_at: string | null }[]
}
export interface VaultClient {
  client_id: string
  role?: string
  actions?: string[]
  status?: string
}

const id = (value: string) => {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value))
    throw new Error('A valid client identifier is required')
  return value
}
export const clientPath = (client: string) => `/clients/${id(client)}`
export const filePath = (client: string) => `/client-vault/${id(client)}`
export async function read<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  try {
    return (await apiClient.get<T>(path, { params })).data
  } catch (error) {
    throw toServiceError(error)
  }
}
export async function command<T>(
  path: string,
  data?: unknown,
  method: 'post' | 'put' = 'post'
): Promise<T> {
  try {
    const config: ManualCommandConfig = { manualCommand: true }
    if (method === 'post' && /^\/client-vault\/scan-jobs\/[0-9a-f-]{36}:publish$/i.test(path))
      config.timeout = 190_000
    return (await apiClient[method]<T>(path, data, config)).data
  } catch (error) {
    throw toServiceError(error)
  }
}
export async function upload(
  client: string,
  file: File,
  metadata: Record<string, unknown>
): Promise<VaultUpload> {
  const form = new FormData()
  form.set('file', file)
  form.set('metadata', JSON.stringify(metadata))
  try {
    return (
      await apiClient.post<VaultUpload>(`${filePath(client)}/uploads`, form, {
        manualCommand: true,
        headers: { 'Content-Type': undefined },
      } as ManualCommandConfig)
    ).data
  } catch (error) {
    throw toServiceError(error)
  }
}
export async function download(client: string, file: VaultFile): Promise<void> {
  try {
    const { data } = await apiClient.get<Blob>(
      `${filePath(client)}/versions/${id(file.version_id)}/content`,
      { responseType: 'blob' }
    )
    const url = URL.createObjectURL(data),
      link = document.createElement('a')
    link.href = url
    link.download = file.filename
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  } catch (error) {
    throw toServiceError(error)
  }
}
